// ProtectedRoute.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import "./ProtectedRoute.css";

const ProtectedRoute = ({
  children,
  redirectTo = "/login"
}) => {
  const { currentUser } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      // Don't check subscription if user is in upgrade process
      const upgradeInProgress = sessionStorage.getItem('upgradeInProgress');
      if (upgradeInProgress === 'true') {

        setSubscriptionStatus('active'); // Allow access during upgrade
        setIsLoading(false);
        return;
      }

      try {
        // Force token refresh to ensure we have a valid token
        const token = await currentUser.getIdToken(true);
        
        const response = await fetch(`/api/subscription/status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          
          // Check if trial has expired
          if (data.subscriptionType === 'trial' && data.trialEndDate) {
            const now = new Date();
            const trialEnd = new Date(data.trialEndDate);
            
            if (now >= trialEnd) {
              // Trial has expired - redirect to subscription required
              setSubscriptionStatus('expired');
            } else {
              // Trial is still active
              setSubscriptionStatus('active');
            }
          } else if (data.subscriptionPlan) {
            // User has a paid plan
            setSubscriptionStatus('active');
          } else {
            // No valid subscription
            setSubscriptionStatus('expired');
          }
        } else if (response.status === 404) {
          const errorData = await response.json();
          if (errorData.isNewUser) {
            // New user - wait a moment and retry

            setTimeout(() => {
              checkSubscriptionStatus();
            }, 2000);
            return;
          } else {
            // No subscription found

            setSubscriptionStatus('expired');
          }
        } else {
          // Other error

          setSubscriptionStatus('expired');
        }
      } catch (error) {

        // Default to expired on error for security
        setSubscriptionStatus('expired');
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [currentUser]);

  // Show loading while checking subscription
  if (isLoading) {
    return (
      <div className="protected-route-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Verifying access...</p>
        </div>
      </div>
    );
  }

  // User not logged in
  if (!currentUser) {
    return <Navigate to={redirectTo} replace />;
  }

  // Trial expired or no valid subscription
  if (subscriptionStatus === 'expired') {
    return <Navigate to="/subscription-required" state={{ trialExpired: true }} replace />;
  }

  // Valid subscription - allow access
  return children;
};

export default ProtectedRoute;
